<template>
  <el-form
    @submit.prevent="validateSubmit('accountForm')"
    :model="account"
    :rules="rules"
    label-position="top"
    ref="accountForm"
    novalidate
  >
    <input-section>
      <div class="flex flex-col md:flex-row justify-end">
        <div class="md:w-4/12 m-2">
          <horizontal-button
            :title="$t('actions.saveChanges')"
            isSuccess
            faIcon="save"
            @click.prevent="validateSubmit('accountForm')"
          />
        </div>

        <div class="md:w-4/12 m-2" v-if="enableClearForm">
          <horizontal-button
            :title="$t('actions.clearButton')"
            isDanger
            faIcon="trash"
            @click.prevent="clearForm('accountForm')"
          />
        </div>
      </div>
    </input-section>

    <!-- @submit errors -->
    <alert-badge
      isDanger
      :title="error.title"
      :body="error.body"
      @dismissed="
        () => {
          error.title = ''
          error.body = ''
        }
      "
    />

    <input-section>
      <h1>{{ t('section') }}</h1>

      <div class="grid grid-cols-1 md:grid-cols-5 gap-4">
        <el-form-item :label="t('enName')" prop="enName" class="input-label col-span-2">
          <el-input
            id="en-name"
            v-model="account.enName"
            placeholder=""
            maxlength="255"
            clearable
            required
            :disabled="account.isSystemAccount"
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('arName')" prop="arName" class="input-label col-span-2">
          <el-input
            id="ar-name"
            v-model="account.arName"
            placeholder=""
            maxlength="255"
            clearable
            required
            :disabled="account.isSystemAccount"
          >
          </el-input>
        </el-form-item>

        <el-form-item :label="t('code')" prop="code" class="input-label">
          <el-input-number
            id="code"
            v-model="account.code"
            :placeholder="t('code')"
            :controls=false
            clearable
            class="w-full"
          >
          </el-input-number>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-7 gap-4">
        <el-form-item :label="t('accountCategory')" class="input-label col-span-2" prop="accountCategory">
          <el-select
            id="account-category"
            v-model="account.accountCategory"
            :trigger-on-focus="true"
            class="w-full"
            placeholder=""
            @change="onAccountCategoryChange"
            filterable
            :disabled="account.isSystemAccount"
          >
            <el-option
              v-for="option in accountCategories"
              :key="option"
              :label="t(option)"
              :value="option"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('parentAccount')" class="input-label col-span-2" prop="parentAccountID">
          <el-select
            id="parent-account"
            v-model="account.parentAccountID"
            :trigger-on-focus="false"
            value-key="name"
            class="w-full"
            filterable
            placeholder=""
            remote
            :remote-method="searchAccounts"
            clearable
            :disabled="account.isSystemAccount"
          >
            <el-option
              v-if="selectedParentAccount"
              :key="selectedParentAccount.id"
              :label="$ctx.locale === 'en' ? selectedParentAccount.enName : selectedParentAccount.arName"
              :value="selectedParentAccount.id"
            ></el-option>

            <el-option
              v-for="option in accounts"
              :key="option.id"
              :label="$ctx.locale === 'en' ? option.enName : option.arName"
              :value="option.id"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('subcategory')" class="input-label col-span-2" prop="subcategory">
          <el-select
            id="account-subcategory"
            v-model="account.accountSubcategory"
            :trigger-on-focus="true"
            class="w-full"
            filterable
            clearable
            placeholder=""
            :disabled="!account.accountCategory || account.isSystemAccount"
          >
            <el-option
              v-for="option in currentSubcategories"
              :key="option"
              :label="t(option)"
              :value="option"
            ></el-option>
          </el-select>
        </el-form-item>

        <el-form-item :label="t('balance')" class="input-label" prop="balance">
          <el-select
            id="balance"
            v-model="account.balance"
            :trigger-on-focus="true"
            class="w-full"
            placeholder=""
            filterable
            :disabled="account.isSystemAccount"
          >
            <el-option
              :label="t('debit')"
              value="debit"
            ></el-option>
            <el-option
              :label="t('credit')"
              value="credit"
            ></el-option>
          </el-select>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 gap-4">
        <el-form-item :label="t('enDescription')" prop="enDescription" class="input-label">
          <el-input
            id="en-description"
            v-model="account.enDescription"
            maxlength="255"
            clearable
          >
          </el-input>
        </el-form-item>
      </div>

      <div class="grid grid-cols-1 gap-4">
        <el-form-item :label="t('arDescription')" prop="arDescription" class="input-label">
          <el-input
            id="ar-description"
            v-model="account.arDescription"
            maxlength="255"
            clearable
          >
          </el-input>
        </el-form-item>
      </div>

      <div class="flex flex-wrap gap-6 mt-4">
        <el-form-item prop="isTransactional" class="mb-0">
          <el-checkbox
            id="is-transactional"
            v-model="account.isTransactional"
            @change="onTransactionalChange"
            :disabled="account.isSystemAccount"
          >
            {{ t('isTransactional') }}
          </el-checkbox>
        </el-form-item>

        <el-form-item prop="isCashierRegister" class="mb-0">
          <el-checkbox
            id="is-cashier-register"
            v-model="account.isCashierRegister"
            :disabled="!account.isTransactional && !account.isSystemAccount"
          >
            {{ t('isCashierRegister') }}
          </el-checkbox>
        </el-form-item>
      </div>
    </input-section>
  </el-form>
</template>

<script lang="ts">
import { defineComponent } from 'vue'

// components
import HorizontalButton from '@/components/HorizontalButton.vue'
import InputSection from '@/components/form/InputSection.vue'
import AlertBadge from '@/components/AlertBadge.vue'

import { ElForm } from 'element-plus'
import { Route } from '@/plugins/navigation'

// models
import { Account, accountI18nMessages } from '@/models/accounting/Account'
import { useI18n } from 'vue-i18n'

export default defineComponent({
  name: 'account-form',

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...accountI18nMessages.en,
          section: 'Account Info',
          enNamePlaceholder: 'For example, overhead expenses',
          arNamePlaceholder: 'For example, overhead expenses',
          enDescriptionPlaceholder: 'For example, this account is used to track overhead expenses',
          arDescriptionPlaceholder: 'For example, this account is used to track overhead expenses',
          skipParent: 'Skip',
          accountCategory: 'Account Category',
          parentAccount: 'Belongs to Account',
          subcategory: 'Subcategory',
          selectSubcategory: 'Select Subcategory',
          assets: "Assets",
          liabilities: "Liabilities",
          equity: "Equity",
          revenue: "Revenue",
          expenses: "Expenses",
          "current-assets": "Current Asset",
          "fixed-assets": "Fixed Asset (PPE)",
          "employee-receivable": "Employee Receivable",
          "contra-assets": "Contra-Asset",
          "current-liabilities": "Current Liability",
          "accrued-expense": "Accrued Expense",
          "long-term-liabilities": "Long-term Liability",
          "owner-equity": "Owner Equity",
          "contra-equity": "Contra-Equity",
          "operating-revenue": "Operating Revenue",
          "contra-revenue": "Contra-Revenue",
          "cogs": "COGS",
          "adjustment": "Adjustment",
          "write-off": "Write-off",
          "contra-cogs": "Contra-COGS",
          "payroll": "Payroll",
          "operating-expenses": "Operating Expenses",
          "selling-expenses": "Selling Expense",
          "depreciation": "Depreciation",
          "other-expenses": "Other Expenses"
        },
        ar: {
          ...accountI18nMessages.ar,
          section: 'معلومات الحساب',
          enNamePlaceholder: 'مثلا: النفقات العامة',
          arNamePlaceholder: 'مثلا: النفقات العامة',
          enDescriptionPlaceholder: 'For example, this account is used to track overhead expenses',
          arDescriptionPlaceholder: 'For example, this account is used to track overhead expenses',
          skipParent: 'تخطي',
          accountCategory: 'فئة الحساب',
          parentAccount: 'تابع للحساب',
          subcategory: 'الفئة الفرعية',
          selectSubcategory: 'اختر الفئة الفرعية',
          assets: "الممتلكات",
          liabilities: "الديون",
          equity: "حقوق الملكية",
          revenue: "الوارد",
          expenses: "المصاريف",
          "current-assets": "أصول متداولة",
          "fixed-assets": "أصول ثابتة",
          "employee-receivable": "سلفة للموظفين",
          "contra-assets": "أصول مقابلة",
          "current-liabilities": "خصوم متداولة",
          "accrued-expense": "مصاريف مستحقة",
          "long-term-liabilities": "خصوم طويلة الأجل",
          "owner-equity": "رأس مال المالك",
          "contra-equity": "حقوق ملكية مقابلة",
          "cogs": "تكلفة المبيعات",
          "adjustment": "تعديلات",
          "write-off": "شطب",
          "contra-cogs": "تكلفة مبيعات مقابلة",
          "payroll": "رواتب وأجور",
          "operating-revenue": "إيرادات تشغيلية",
          "contra-revenue": "إيرادات مقابلة",
          "operating-expenses": "مصاريف تشغيلية",
          "selling-expenses": "مصاريف بيع",
          "depreciation": "اندثار",
          "other-expenses": "مصاريف أخرى"
        }
      },
      unescape: 'global'
    })

    return { t }
  },

  components: {
    AlertBadge,
    HorizontalButton,
    InputSection
  },
  props: {
    initAccount: {
      type: Account,
      required: true
    },
    submitAction: {
      type: Function,
      required: true
    },
    successMsg: {
      type: String,
      required: true
    },
    enableClearForm: {
      type: Boolean,
      required: false
    },
    clearFormOnSubmit: {
      type: Boolean,
      default: false
    }
  },

  async beforeMount() {
    if (!this.account.parentAccount) {
      // create an empty parent
      // this.account.parentAccount = new Account()
    }
  },

  data() {
    const accounts = new Array<Account>()
    const accountCategories = ['assets', 'liabilities', 'equity', 'revenue', 'expenses']

    const subcategoriesMap: Record<string, string[]> = {
      "assets": [
        "current-assets",
        "fixed-assets",
        "employee-receivable",
        "fixed-assets",
        "contra-assets",
      ],
      "liabilities": [
        "current-liabilities",
        "accrued-expense",
        "long-term-liabilities",
      ],
      "equity": [
        "owner-equity",
        "contra-equity",
        "equity",
      ],
      "revenue": [
        "operating-revenue",
        "contra-revenue",
      ],
      "expenses": [
        "cogs",
        "adjustment",
        "write-off",
        "contra-cogs",
        "payroll",
        "operating-expenses",
        "selling-expenses",
        "depreciation",
        "other-expenses",
      ],
    }

    return {
      account: this.initAccount,
      accounts,
      error: { title: '', body: '' },
      accountCategories,
      subcategoriesMap,
      selectedParentAccount: this.initAccount.parentAccount,
      rules: {
        enName: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ],
        arName: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          },
          {
            max: 255,
            message: this.$t('validation.maxLength', { len: 255 }),
            trigger: 'blur'
          }
        ],
        isSystemAccount: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ],
        accountCategory: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ],
        balance: [
          {
            required: true,
            message: this.$t('validation.required'),
            trigger: 'blur'
          }
        ]
      }
    }
  },

  computed: {
    currentSubcategories(): string[] {
      if (!this.account.accountCategory) {
        return []
      }
      return this.subcategoriesMap[this.account.accountCategory] || []
    }
  },

  methods: {
    async searchAccounts(query: string) {
      try {
        const url = `${Account.ENDPOINT}?q=${query}&account-category=${this.account.accountCategory?.toLowerCase()}`
        this.accounts = await this.$http.get<Account[]>(url)
      } catch (error: any) {
        this.$alertModal.showDanger({ title: error.title || 'Error' })
        this.accounts = []
      }
    },

    clearForm(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form.resetFields()
    },

    async validateSubmit(formName: string) {
      const form = this.$refs[formName] as typeof ElForm
      form?.validate(async (valid: boolean) => {
        if (!valid) {
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        }

        const resp = await this.submitAction(this.account)

        if (resp) {
          this.error.title = resp?.title
          this.error.body = resp?.body
          document.getElementById('main')?.scrollTo({ top: 0 })
          return false
        } else {
          if (this.clearFormOnSubmit) {
            this.clearForm(formName)
          }

          this.$alertModal.showSuccess({ title: this.successMsg, body: '' })
          this.$goTo(Route.ACCOUNTING_ACCOUNTS)
        }
        return true
      })
    },

    onAccountCategoryChange() {
      this.account.parentAccountID = ''
      this.account.accountSubcategory = undefined
      this.selectedParentAccount = undefined
      this.searchAccounts('');

      // Auto-set balance based on account category
      // Assets, Expenses -> Debit
      // Liabilities, Equity, Revenue -> Credit
      if (this.account.accountCategory === 'assets' || this.account.accountCategory === 'expenses') {
        this.account.balance = 'debit'
      } else if (this.account.accountCategory === 'liabilities' || this.account.accountCategory === 'equity' || this.account.accountCategory === 'revenue') {
        this.account.balance = 'credit'
      }
    },

    onTransactionalChange(value: boolean) {
      if (!value && this.account.isCashierRegister) {
        this.account.isCashierRegister = false
      }
    }
  }
})
</script>

<style scoped>
h1,
h2,
h3 {
  @apply text-lg  font-bold;
}

form {
  @apply flex flex-col items-center;
}
</style>
